import Api from "../services/AxiosService";
import { unwrap } from "./envelope";
import type { ApplicationDetail, FastenerBrandCode } from "./applications";

// svc-10 `/v1/calculate` (gateway routes /v1/calculate -> calculation). Admin
// "Cost Model Tools" evaluator reuses the same preview endpoint the customer
// comparison uses; the response carries the per-application breakdown +
// aggregate KPIs + resolved machine cost that make up the Result + Trace.
// Types kept local per the existing api/*.ts convention.

export type RegionCode = "EU" | "NA" | "ASIA";
export type MachineCostMode = "shared" | "dedicated";

export type CalculateFastenerInput = {
  brand: FastenerBrandCode;
  type?: string | null;
  qtyPerPart?: string;
  qtyPerPartA?: string;
  qtyPerPartB?: string;
};

export type CalculateApplicationInput = {
  applicationId?: string;
  name?: string | null;
  annualVolume?: string;
  batchSize?: string;
  fasteners: CalculateFastenerInput[];
};

export type CalculatePreviewRequest = {
  region: RegionCode;
  productId: string;
  machineCostMode: MachineCostMode;
  applications: CalculateApplicationInput[];
};

export type ComparisonSummary = {
  costPerPart: string;
  machineCostPerPart: string;
  workerCostPerPart: string;
  qualityCostPerPart: string;
  machineUtilization?: string;
  workerUtilization?: string;
  requiredMachines: string;
  requiredOperators: string;
};

export type PerAppCostBreakdown = ComparisonSummary & {
  applicationId?: string;
  applicationName: string | null;
  batchesPerYear: string;
};

export type CalculateUnavailableReason =
  | "no_cost_model"
  | "asia_pricing_unavailable"
  | "machine_price_unavailable";

export type CalculatePreviewResponse =
  | {
      ok: true;
      available: true;
      result: {
        perApp: PerAppCostBreakdown[];
        aggregate: {
          investmentCost: string;
          operatorCost: string;
          qualityCost: string;
          totalCost: string;
        };
        summary: ComparisonSummary;
        currencyCode: string;
      };
    }
  | {
      ok: true;
      available: false;
      reason: CalculateUnavailableReason;
      details?: Record<string, unknown>;
    };

/** Map an admin application detail to the calculate request shape. */
export function applicationToCalculateInput(
  detail: ApplicationDetail,
): CalculateApplicationInput {
  return {
    applicationId: detail.applicationId,
    name: detail.name,
    annualVolume:
      detail.annualVolume != null ? String(detail.annualVolume) : undefined,
    batchSize: detail.batchSize != null ? String(detail.batchSize) : undefined,
    fasteners: detail.fasteners
      .filter((f): f is typeof f & { brand: FastenerBrandCode } => !!f.brand)
      .map((f) => ({
        brand: f.brand,
        type: f.type,
        ...(f.qtyPerPart != null ? { qtyPerPart: String(f.qtyPerPart) } : {}),
        ...(f.qtyPerPartA != null ? { qtyPerPartA: String(f.qtyPerPartA) } : {}),
        ...(f.qtyPerPartB != null ? { qtyPerPartB: String(f.qtyPerPartB) } : {}),
      })),
  };
}

export function isApplicationValidForCalculate(detail: ApplicationDetail): boolean {
  if (!detail.annualVolume || detail.annualVolume <= 0) return false;
  if (!detail.batchSize || detail.batchSize <= 0) return false;
  if (!detail.fasteners.length) return false;
  return detail.fasteners.some((f) => {
    if (!f.brand) return false;
    return (f.qtyPerPart ?? 0) > 0 || (f.qtyPerPartA ?? 0) > 0 || (f.qtyPerPartB ?? 0) > 0;
  });
}

export async function postCalculatePreview(
  body: CalculatePreviewRequest,
): Promise<CalculatePreviewResponse> {
  const res = await Api.post("/v1/calculate", body);
  return unwrap<CalculatePreviewResponse>(res.data);
}
